import { useCallback, useEffect, useRef, useState } from "react";
import { Game } from "../projects/space_invaders/game";
import { Controls } from "../projects/space_invaders/input";
import { Autopilot } from "../projects/space_invaders/autopilot";
import { GameConfig } from "../projects/space_invaders/config";

export type HeroMode = "static" | "autoplay" | "manual";

export interface UseInvadersHeroOptions {
  config: GameConfig;
  /** Phones get the demo only — no keyboard capture, no fullscreen. */
  interactive: boolean;
}

export interface InvadersHero {
  containerRef: React.RefObject<HTMLDivElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  mode: HeroMode;
  takeControl: () => void;
  release: () => void;
  reset: () => void;
  toggleFullscreen: () => void;
  isFullscreen: boolean;
  canFullscreen: boolean;
}

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function useInvadersHero(options: UseInvadersHeroOptions): InvadersHero {
  const { config, interactive } = options;
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const gameRef = useRef<Game | null>(null);
  const controlsRef = useRef<Controls | null>(null);
  const autopilotRef = useRef<Autopilot | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const rafRef = useRef(0);
  const lastTimeRef = useRef(0);
  const runningRef = useRef(false);
  const visibleRef = useRef(true);

  const [mode, setMode] = useState<HeroMode>("static");
  const modeRef = useRef<HeroMode>("static");
  const [isFullscreen, setIsFullscreen] = useState(false);

  const setModeBoth = useCallback((next: HeroMode) => {
    modeRef.current = next;
    setMode(next);
  }, []);

  const stopLoop = useCallback(() => {
    runningRef.current = false;
    window.cancelAnimationFrame(rafRef.current);
  }, []);

  const startLoop = useCallback(() => {
    if (runningRef.current || !visibleRef.current) return;
    if (prefersReducedMotion() && modeRef.current !== "manual") return;
    runningRef.current = true;
    lastTimeRef.current = 0;

    const step = (time: number) => {
      if (!runningRef.current) return;
      const game = gameRef.current;
      const ctx = ctxRef.current;
      if (!game || !ctx) return;

      const dt = lastTimeRef.current
        ? Math.min((time - lastTimeRef.current) / 1000, 0.05)
        : 0;
      lastTimeRef.current = time;

      const manual = modeRef.current === "manual";
      const source = manual ? controlsRef.current! : autopilotRef.current!;
      if (!manual) autopilotRef.current!.update(dt, () => game.snapshot());

      game.update(dt, source);
      game.draw(ctx);
      rafRef.current = window.requestAnimationFrame(step);
    };

    rafRef.current = window.requestAnimationFrame(step);
  }, []);

  const release = useCallback(() => {
    if (modeRef.current !== "manual") return;
    controlsRef.current?.setEnabled(false);
    gameRef.current?.setChrome({ hud: false });
    setModeBoth("autoplay");
  }, [setModeBoth]);

  const takeControl = useCallback(() => {
    if (!interactive || modeRef.current === "manual") return;
    controlsRef.current?.setEnabled(true);
    gameRef.current?.setChrome({ hud: true });
    containerRef.current?.focus();
    setModeBoth("manual");
    startLoop();
  }, [interactive, setModeBoth, startLoop]);

  const reset = useCallback(() => gameRef.current?.restart(), []);

  const toggleFullscreen = useCallback(() => {
    const element = containerRef.current;
    if (!element) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else element.requestFullscreen?.();
  }, []);

  // Build the engine and paint the composed still frame.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = config.canvas.width;
    canvas.height = config.canvas.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;

    const game = new Game(config);
    ctxRef.current = ctx;
    gameRef.current = game;
    autopilotRef.current = new Autopilot();
    controlsRef.current = new Controls({
      target: containerRef.current ?? window,
      enabled: false,
    });
    game.draw(ctx);

    return () => {
      stopLoop();
      controlsRef.current?.destroy();
      gameRef.current = null;
      controlsRef.current = null;
      autopilotRef.current = null;
      ctxRef.current = null;
    };
  }, [config, stopLoop]);

  // The hero sits at the top of the page, so it is already on screen at load.
  // The first scroll is the signal that the visitor is actually looking.
  useEffect(() => {
    const begin = () => {
      if (modeRef.current !== "static") return;
      setModeBoth("autoplay");
      startLoop();
    };
    const events: Array<keyof WindowEventMap> = ["scroll", "wheel", "touchmove"];
    events.forEach((event) =>
      window.addEventListener(event, begin, { once: true, passive: true })
    );
    return () => events.forEach((event) => window.removeEventListener(event, begin));
  }, [setModeBoth, startLoop]);

  // Never burn frames on a hero nobody can see.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries[0]?.isIntersecting ?? true;
        visibleRef.current = visible;
        if (visible) {
          if (modeRef.current !== "static") startLoop();
        } else {
          stopLoop();
          release();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [startLoop, stopLoop, release]);

  // No Release button by design, so manual mode has to end on its own the
  // moment the visitor's attention moves — otherwise captured arrow keys and
  // space would stop the page scrolling with no way out.
  useEffect(() => {
    if (mode !== "manual") return;
    const element = containerRef.current;
    if (!element) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") release();
    };
    const onPointerDownOutside = (e: Event) => {
      if (!element.contains(e.target as Node)) release();
    };
    const onFocusOut = (e: FocusEvent) => {
      if (!element.contains(e.relatedTarget as Node)) release();
    };

    element.addEventListener("keydown", onKeyDown);
    element.addEventListener("focusout", onFocusOut as EventListener);
    document.addEventListener("pointerdown", onPointerDownOutside);
    return () => {
      element.removeEventListener("keydown", onKeyDown);
      element.removeEventListener("focusout", onFocusOut as EventListener);
      document.removeEventListener("pointerdown", onPointerDownOutside);
    };
  }, [mode, release]);

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  return {
    containerRef,
    canvasRef,
    mode,
    takeControl,
    release,
    reset,
    toggleFullscreen,
    isFullscreen,
    canFullscreen: interactive && Boolean(document.fullscreenEnabled),
  };
}
